import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const ESTADOS_ACTIVOS = ["pendiente", "procesando"];
const POLL_MS = 3000;

/**
 * Sondea el estado de un job de generación masiva de IA (rutinas u hoja de
 * vida) mientras esté pendiente/procesando: recupera el job activo al montar
 * (por si el usuario navegó fuera y volvió), lo crea al llamar `iniciar`, y
 * deja de sondear cuando termina (completado/fallido).
 */
export function useAiMasivoJob({ tipo, endpoint, onCompleted }) {
  const API = process.env.REACT_APP_API_URL + "/ia";
  const [job, setJob] = useState(null);
  const intervalRef = useRef(null);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const getToken = () => localStorage.getItem("token");

  const detenerPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const consultarJob = useCallback(
    async (jobId) => {
      try {
        const res = await axios.get(`${API}/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const actualizado = res.data;
        setJob(actualizado);
        if (!ESTADOS_ACTIVOS.includes(actualizado.estado)) {
          detenerPolling();
          if (onCompletedRef.current) onCompletedRef.current(actualizado);
        }
      } catch {
        detenerPolling();
      }
    },
    [API, detenerPolling],
  );

  const iniciarPolling = useCallback(
    (jobId) => {
      detenerPolling();
      intervalRef.current = setInterval(() => consultarJob(jobId), POLL_MS);
    },
    [consultarJob, detenerPolling],
  );

  useEffect(() => {
    let vigente = true;

    (async () => {
      try {
        const res = await axios.get(`${API}/jobs`, {
          params: { tipo },
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const activo = (res.data || []).find((j) =>
          ESTADOS_ACTIVOS.includes(j.estado),
        );
        if (vigente && activo) {
          setJob(activo);
          iniciarPolling(activo._id);
        }
      } catch {
        // Si falla la consulta inicial, el usuario aún puede intentar generar.
      }
    })();

    return () => {
      vigente = false;
      detenerPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  const iniciar = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API}${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const jobCreado = res.data?.job;
      if (jobCreado) {
        setJob(jobCreado);
        iniciarPolling(jobCreado._id);
      }
      return { ok: true, message: res.data?.message };
    } catch (err) {
      const jobExistente = err.response?.data?.job;
      if (err.response?.status === 409 && jobExistente) {
        setJob(jobExistente);
        iniciarPolling(jobExistente._id);
      }
      const backendMessage =
        err.response?.data?.message ||
        err.message ||
        "Error al iniciar la generación masiva";
      return { ok: false, duplicate: err.response?.status === 409, message: backendMessage };
    }
  }, [API, endpoint, iniciarPolling]);

  const enProceso = !!job && ESTADOS_ACTIVOS.includes(job.estado);

  return { job, enProceso, iniciar };
}
